"use client"
import type { DialogDescription, DialogTitle } from "@radix-ui/react-dialog"
import type React from "react"
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  createContext,
  useContext,
  useMemo,
} from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { Drawer as SheetPrimitive } from "vaul"
import { Close } from "../../icons"
import { overlayVariants } from "../../styles/utils/overlay"
import { classNames } from "../../utils"
import { Block, type BlockProps } from "../Block"
import { Button } from "../Button"
import { FlexCenter, type FlexCenterProps } from "../FlexCenter"
import { Media } from "../Media"
import { TextHeading } from "../Text"

// z-sheet index
const nestedClassName = classNames("z-[calc(var(--z-sheet)+1)]")

const sheetVariants = tv({
  base: classNames(
    "fixed inset-y-0 z-sheet flex max-h-full w-full flex-col focus-visible:outline-hidden lg:max-w-[460px]",
    "bg-bg-app shadow-sm",
  ),
  variants: {
    direction: {
      left: "left-0",
      right: "right-0",
    },
    nested: {
      true: nestedClassName,
    },
  },
})

export type SideSheetProps = Pick<
  ComponentProps<typeof SheetPrimitive.Root>,
  | "open"
  | "onOpenChange"
  | "modal"
  | "shouldScaleBackground"
  | "disablePreventScroll"
  | "repositionInputs"
  | "handleOnly"
> &
  Omit<ComponentProps<typeof SheetPrimitive.Content>, "content"> &
  VariantProps<typeof sheetVariants> & {
    content: React.ReactNode
    withCloseIcon?: boolean
    ref?: React.Ref<HTMLDivElement>
    overrides?: {
      Overlay: ComponentProps<typeof SheetPrimitive.Overlay>
    }
  }

const SideSheetContext = createContext<{
  direction: "left" | "right"
  withCloseIcon: boolean
}>({
  direction: "left",
  withCloseIcon: true,
})

function SideSheet({
  open,
  onOpenChange,
  content,
  children,
  className,
  overrides,
  modal,
  direction = "left",
  withCloseIcon = true,
  repositionInputs = false,
  handleOnly = true,
  nested,
  ...props
}: SideSheetProps) {
  const value = useMemo(
    () => ({ direction, withCloseIcon }),
    [direction, withCloseIcon],
  )

  return (
    <SideSheetContext.Provider value={value}>
      <SheetPrimitive.Root
        direction={direction}
        // Disable prevent scroll and noBodyStyles is needed to prevent layout shift.
        // Seems that scrolling is prevented anyways even if this is disabled.
        // https://github.com/emilkowalski/vaul/issues/285
        disablePreventScroll
        handleOnly={handleOnly}
        modal={modal}
        noBodyStyles
        onOpenChange={onOpenChange}
        open={open}
        repositionInputs={repositionInputs}
      >
        <SheetPrimitive.Trigger asChild>{children}</SheetPrimitive.Trigger>
        <SheetPrimitive.Portal>
          <SheetPrimitive.Overlay
            data-testid="Overlay"
            {...overrides?.Overlay}
            className={classNames(
              overlayVariants(),
              nested && nestedClassName,
              overrides?.Overlay.className,
            )}
          />

          <SheetPrimitive.Content
            className={classNames(
              sheetVariants({ direction, nested }),
              className,
            )}
            {...props}
          >
            {content}
          </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
      </SheetPrimitive.Root>
    </SideSheetContext.Provider>
  )
}

export const SideSheetHeader = ({
  className,
  children,
  ...props
}: FlexCenterProps) => {
  const { direction, withCloseIcon } = useContext(SideSheetContext)

  return (
    <FlexCenter
      className={classNames(
        "relative min-h-17 justify-center p-4 lg:justify-normal lg:p-6",
        className,
      )}
      {...props}
    >
      {children}
      {withCloseIcon && (
        <>
          <Media lessThan="lg">
            <SheetPrimitive.Close asChild>
              <Button
                aria-label="Close"
                className={classNames(
                  "absolute inset-y-0 top-3.5 z-sheet flex items-center",
                  direction === "left" ? "right-4" : "left-4",
                )}
                icon={Close}
                rounded
                variant="secondary"
              />
            </SheetPrimitive.Close>
          </Media>
          <Media greaterThanOrEqual="lg">
            <SheetPrimitive.Close
              className={classNames(
                "absolute inset-y-0 top-[22px] z-sheet flex aspect-square size-7 items-center justify-center rounded bg-bg-app focus-visible:outline-hidden",
                direction === "left"
                  ? "right-[calc((theme(spacing.7)+theme(spacing.6))*-1)]"
                  : "left-[calc((theme(spacing.7)+theme(spacing.6))*-1)]",
              )}
            >
              <Close aria-label="Close" fill="text-primary" />
            </SheetPrimitive.Close>
          </Media>
        </>
      )}
    </FlexCenter>
  )
}

export function SideSheetHeaderTitle({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof DialogTitle> & {
  ref?: React.Ref<HTMLHeadingElement>
}) {
  return (
    <SheetPrimitive.Title asChild {...props}>
      <TextHeading className={className} size="sm">
        {children}
      </TextHeading>
    </SheetPrimitive.Title>
  )
}

export function SideSheetBody({
  children,
  className,
  ...props
}: BlockProps & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <Block
      className={classNames("overflow-y-auto px-2 lg:px-6", className)}
      {...props}
    >
      {children}
    </Block>
  )
}

export function SideSheetDescription({
  className,
  content,
  ...props
}: ComponentPropsWithoutRef<typeof DialogDescription> & {
  ref?: React.Ref<HTMLParagraphElement>
}) {
  return (
    <SheetPrimitive.Description className={className} {...props}>
      {content}
    </SheetPrimitive.Description>
  )
}

/**
 * Displays content that complements the main content of the screen.
 * The bottom variant of this is a common pattern for mobile devices.
 */
export { SideSheet }
